<template>
  <header ref="header" class="mobile-site-header">
    <div class="mobile-bar">
      <router-link :to="homePath" class="mobile-brand" aria-label="KAMS home" @click.native="closePanels">
        <img src="@/assets/image/logo_white.png" alt="KAMS Lighting">
      </router-link>
      <div class="mobile-tools">
        <button type="button" :aria-label="$t('search.language_4')" :aria-expanded="searchOpen" @click="toggleSearch"><i class="el-icon-search" /></button>
        <button type="button" :aria-label="$t('personalCenter.favorites')" @click="accountAction('favorites')">
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" :fill="favCount ? '#d71518' : 'none'" stroke="currentColor" stroke-width="1.5" /></svg>
          <span v-if="favCount" class="mobile-badge">{{ favCount > 99 ? '99+' : favCount }}</span>
        </button>
        <button ref="menuTrigger" type="button" aria-label="Open navigation" :aria-expanded="menuOpen" aria-controls="mobile-site-menu" @click="openMenu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
        </button>
      </div>
    </div>
    <SearchDialog :visible.sync="searchOpen" :get-header-element="() => $refs.header" />
    <el-drawer :visible.sync="menuOpen" direction="rtl" size="min(420px, 100vw)" :z-index="12000" append-to-body :with-header="false" custom-class="kams-mobile-drawer" @closed="restoreMenuFocus">
      <div id="mobile-site-menu" class="mobile-menu-shell">
        <div class="mobile-menu-heading">
          <router-link :to="homePath" class="mobile-brand" @click.native="closePanels"><img src="@/assets/image/logo_white.png" alt="KAMS Lighting"></router-link>
          <button type="button" class="mobile-close" aria-label="Close navigation" @click="menuOpen = false"><i class="el-icon-close" /></button>
        </div>
        <div class="mobile-menu-scroll">
          <button type="button" class="mobile-menu-search" @click="toggleSearch"><i class="el-icon-search" /><span>{{ $t('search.language_4') }}</span><i class="el-icon-right" /></button>
          <nav class="mobile-menu-nav" aria-label="Main navigation">
            <div v-for="item in menuItems" :key="item.url" class="mobile-menu-row">
              <template v-if="isProducts(item)">
                <button type="button" class="mobile-menu-link" :class="{ 'is-current': isActive(item) }" :aria-expanded="productsOpen" aria-controls="mobile-product-categories" @click="productsOpen = !productsOpen">
                  <span>{{ item.name }}</span><i :class="productsOpen ? 'el-icon-minus' : 'el-icon-plus'" />
                </button>
                <div v-if="productsOpen" id="mobile-product-categories" class="mobile-product-categories">
                  <router-link :to="productsPath" class="mobile-all-products" @click.native="closePanels">All Products<i class="el-icon-right" /></router-link>
                  <div class="mobile-category-groups">
                  <div v-if="loading" class="mobile-category-status" aria-live="polite"><i class="el-icon-loading" aria-label="Loading categories" /></div>
                  <button v-else-if="loadFailed" type="button" class="mobile-retry" @click="loadCategories">{{ $t('personalCenter.retryBtn') }}</button>
                  <div v-for="category in categories" :key="category.id" class="mobile-category">
                    <h3 class="mobile-category-heading">{{ category.categoryName }}</h3>
                    <div class="mobile-subcategories">
                      <router-link v-for="child in visibleChildren(category)" :key="child.id" :to="categoryRoute(child, category)" @click.native="closePanels">{{ child.categoryName }}</router-link>
                    </div>
                  </div>
                  </div>
                </div>
              </template>
              <router-link v-else :to="item.url" class="mobile-menu-link" :class="{ 'is-current': isActive(item) }" :aria-current="isActive(item) ? 'page' : null" @click.native="closePanels">{{ item.name }}<i class="el-icon-right" /></router-link>
            </div>
          </nav>
          <div class="mobile-account">
            <button type="button" class="mobile-account-link" @click="accountAction(isLogin ? 'personal' : 'login')">
              <img v-if="isLogin && avatar" :src="avatar" alt="" class="mobile-avatar">
              <i v-else class="el-icon-user" />
              <span>{{ isLogin ? $t('login.personal') : $t('login.loging') }}</span><i class="el-icon-right" />
            </button>
            <button v-if="isLogin" type="button" class="mobile-account-link" @click="accountAction('logout')"><i class="el-icon-switch-button" /><span>{{ $t('login.logout') }}</span></button>
          </div>
          <div class="mobile-menu-language">
            <i class="el-icon-position" aria-hidden="true" />
            <LanguageSwitcher :refresh-on-change="true" @language-changed="languageChanged" @refresh-requests="$emit('refresh-requests')" />
          </div>
        </div>
      </div>
    </el-drawer>
  </header>
</template>

<script>
import SearchDialog from '@/components/search/SearchDialog.vue'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import { getProductType } from '@/api/home'
import { buildTree } from '@/utils/index'
import { productCategoryRoute } from '@/utils/productCategoryRoute'

export default {
  name: 'MobileHeader',
  components: { SearchDialog, LanguageSwitcher },
  props: {
    navItems: { type: Array, default: () => [] },
    isLogin: Boolean,
    avatar: { type: String, default: '' },
    favCount: { type: Number, default: 0 }
  },
  data: () => ({ menuOpen: false, searchOpen: false, productsOpen: false, categories: [], loading: false, loadFailed: false, requestVersion: 0 }),
  computed: {
    homePath() { return `/${this.$route.params.lang || this.$store.state.user.lang || 'en'}/` },
    productsPath() { return `${this.homePath}products` },
    menuItems() { return this.navItems.filter(item => item.name !== this.$t('search.language_4')) }
  },
  watch: {
    menuOpen(open) { document.body.classList.toggle('kams-mobile-menu-open', open) },
    '$route.fullPath'() { this.closePanels() },
    '$i18n.locale'() { this.loadCategories() }
  },
  created() { this.loadCategories() },
  beforeDestroy() {
    this.requestVersion++
    document.body.classList.remove('kams-mobile-menu-open')
  },
  methods: {
    async loadCategories() {
      const version = ++this.requestVersion
      this.loading = true
      this.loadFailed = false
      try {
        const response = await getProductType({ pageNum: 1, pageSize: 0 })
        if (version !== this.requestVersion) return
        if (response.code !== 200 || !Array.isArray(response.rows)) throw new Error('Categories unavailable')
        this.categories = buildTree(response.rows).filter(item => Number(item.categoryState) === 1)
      } catch (error) {
        if (version === this.requestVersion) { this.loadFailed = true; this.categories = [] }
      } finally { if (version === this.requestVersion) this.loading = false }
    },
    visibleChildren(category) { return (category.children || []).filter(item => Number(item.categoryState) === 1) },
    categoryRoute(item, root) { return productCategoryRoute(item, root, this.productsPath) },
    isProducts(item) { return item.url.split('?')[0] === this.productsPath },
    isActive(item) {
      const path = item.url.split('?')[0]
      return this.$route.path === path || (path !== this.homePath && this.$route.path.startsWith(`${path}/`))
    },
    openMenu() { this.searchOpen = false; this.menuOpen = true },
    closePanels() { this.menuOpen = false; this.searchOpen = false },
    toggleSearch() { this.menuOpen = false; this.searchOpen = !this.searchOpen },
    accountAction(action) { this.closePanels(); this.$emit(action) },
    languageChanged(event) { this.menuOpen = false; this.$emit('language-changed', event) },
    restoreMenuFocus() { if (!this.searchOpen && this.$refs.menuTrigger) this.$refs.menuTrigger.focus() }
  }
}
</script>

<style scoped>
.mobile-site-header { position: fixed; inset: 0 0 auto; z-index: 1001; background: #000; color: #fff; padding-top: env(safe-area-inset-top, 0px); }
.mobile-bar { height: 64px; padding: 0 16px; display: flex; align-items: center; justify-content: space-between; gap: 12px; box-sizing: border-box; }
.mobile-brand { display: inline-flex; align-items: center; flex-shrink: 0; min-height: 44px; }
.mobile-brand img { display: block; width: 100px; height: auto; }
.mobile-tools { display: flex; align-items: center; gap: 2px; }
.mobile-tools button, .mobile-close { position: relative; display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; padding: 0; border: 0; background: transparent; color: inherit; font-size: 23px; cursor: pointer; }
.mobile-badge { position: absolute; top: 1px; right: 0; min-width: 16px; padding: 0 3px; border-radius: 8px; background: #d71518; color: white; font-size: 10px; line-height: 16px; }
.mobile-menu-shell { display: flex; flex-direction: column; height: 100%; color: #202020; background: #fff; font-size: 16px; line-height: 1.5; }
.mobile-menu-heading { display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; padding: env(safe-area-inset-top, 0px) 20px 0; min-height: 64px; background: #000; color: #fff; }
.mobile-menu-scroll { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain; padding: 20px 24px max(24px, env(safe-area-inset-bottom, 0px)); }
.mobile-menu-search { display: flex; align-items: center; gap: 12px; width: 100%; min-height: 48px; padding: 0 16px; border: 1px solid #dedede; background: #f7f7f7; color: #666; font: inherit; text-align: left; cursor: pointer; }
.mobile-menu-search span { flex: 1; }
.mobile-menu-nav { margin-top: 20px; }
.mobile-menu-row { border-bottom: 1px solid #e9e9e9; }
.mobile-menu-nav .mobile-menu-link { display: flex; align-items: center; justify-content: space-between; gap: 12px; width: 100%; min-height: 54px; box-sizing: border-box; padding: 12px 0; margin: 0; border: 0; background: transparent; color: #222; font: inherit; font-size: 18px; text-align: left; text-decoration: none; cursor: pointer; }
.mobile-menu-link.is-current { font-weight: 600; }
.mobile-menu-link.is-current::before { content: ''; width: 3px; height: 18px; background: #d71518; flex-shrink: 0; }
.mobile-menu-link span { flex: 1; }
.mobile-menu-link > i { margin-left: auto; font-size: 16px; color: #888; }
.mobile-product-categories { display: grid; grid-template-columns: 82px minmax(0, 1fr); align-items: start; gap: 12px; padding: 0 0 16px; }
.mobile-category-groups { min-width: 0; }
.mobile-category + .mobile-category { margin-top: 16px; }
.mobile-category-heading { margin: 0; padding: 10px 0; min-height: 46px; box-sizing: border-box; font-size: 15px; line-height: 26px; font-weight: 600; overflow-wrap: anywhere; }
.mobile-product-categories a, .mobile-category button, .mobile-retry { display: flex; align-items: center; justify-content: space-between; gap: 12px; width: 100%; min-height: 46px; box-sizing: border-box; padding: 10px 12px; border: 0; background: #f7f7f7; color: #333; font: inherit; font-size: 15px; text-align: left; text-decoration: none; cursor: pointer; }
.mobile-product-categories .mobile-all-products { padding: 10px 0; background: transparent; font-size: 13px; text-decoration: underline; text-underline-offset: 4px; white-space: nowrap; }
.mobile-all-products i { display: none; }
.mobile-subcategories { display: flex; flex-wrap: wrap; gap: 0 16px; padding-left: 10px; border-left: 1px solid #ddd; }
.mobile-subcategories a { width: auto; max-width: 100%; padding: 10px 0; background: #fff; font-size: 14px; overflow-wrap: anywhere; }
.mobile-category-status { padding: 16px; text-align: center; }
.mobile-account { margin-top: 24px; }
.mobile-account-link { display: flex; align-items: center; gap: 12px; min-height: 48px; width: 100%; padding: 8px 0; border: 0; background: #fff; color: #333; font: inherit; text-align: left; cursor: pointer; }
.mobile-account-link span { flex: 1; }
.mobile-avatar { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; }
.mobile-menu-language { display: flex; align-items: center; gap: 8px; margin-top: 20px; padding-top: 16px; border-top: 1px solid #e9e9e9; }
.mobile-menu-language /deep/ .current-language { color: #222 !important; min-height: 44px; padding: 0 8px; box-sizing: border-box; }
.mobile-menu-language /deep/ .current-language:hover { background: #f5f5f5; }
button:focus-visible, a:focus-visible { outline: 2px solid #999; outline-offset: -2px; }
@media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
</style>

<style>
.kams-mobile-drawer .el-drawer__body { overflow: hidden; background: #fff; }
body.kams-mobile-menu-open .__jivoDesktopButton { visibility: hidden !important; pointer-events: none; }
</style>
